import React, { useState } from "react"
import styled from "styled-components"
import themeGet from "@styled-system/theme-get"
import compose from "recompose/compose"
import { t } from "@/i18n"
import { withi18n } from "@/helpers/hoc/withi18n"
import { Box } from "@xingternal/winery-ui/lib/Box"
import { Flex } from "@xingternal/winery-ui/lib/Flex"
import { Text } from "@xingternal/winery-ui/lib/Text"
import { Sidebar } from "@xingternal/winery-ui/lib/Sidebar"
import { Icon } from "@xingternal/winery-ui/lib/Icon"
import {
  getAdminNavItems,
  DELAY,
  FADEIN,
  FADEOUT,
  EMPTY_ICON_SPACE,
} from "../OTMSidebarItems/constants"

const i18n = {
  administrationText: "360_RECRUITER_INSIGHTS_OTM_SIDEBAR_ADMINISTRATION",
}

const HoverAnchor = styled(Box)``

const HoverContainer = styled(Box).attrs({
  position: "absolute",
  left: "48px",
  boxShadow: 2,
  backgroundColor: "white",
  minWidth: 170,
  zIndex: "navigationLevel",
})`
  cursor: pointer;
  transform: translateX(themeGet("space.8"));
`

const AdminArrow = styled(Icon).attrs({
  name: "ShapeArrowRight",
})`
  transform: rotate(
    ${({ rotated }) => (rotated === "true" ? "90deg" : "0deg")}
  );
  transition: all 0.2s;
  margin-left: ${themeGet("space.3")};
`

const StyledText = styled(Text).attrs({
  color: "secondaryText",
  p: 3,
  paddingLeft: EMPTY_ICON_SPACE,
})``

const BaseHoverText = styled(StyledText).attrs({
  pl: 3,
})`
  &:hover {
    background-color: ${themeGet("colors.grey50")};
  }
`

const AnimatedItem = styled(Sidebar.Item)`
  transition:
    max-height 0.25s,
    opacity 0.25s;
  transition-delay: ${(props) => props.delay}s;
  overflow: hidden;
  ${(props) => (props.expanded ? FADEIN : FADEOUT)}
`

const OTMAdminSidebarItems = ({ isCollapsed, permissions }) => {
  const { xtm: isXtmAdmin, xtp: isXtpAdmin } = permissions.admin
  const [isAdminHovered, setIsAdminHovered] = useState(false)
  const [isAdminCollapsed, setIsAdminCollapsed] = useState(true)

  const handleHoverIn = () => setIsAdminHovered(true)
  const handleHoverOut = () => setIsAdminHovered(false)
  const toggleAdmin = () => {
    if (!isCollapsed) {
      setIsAdminCollapsed((prev) => !prev)
    }
  }

  const renderAdminItems = () =>
    getAdminNavItems(isXtmAdmin, isXtpAdmin).map(
      ({ id, href, label, target }, index) => {
        const displayAdminItem = !isCollapsed && !isAdminCollapsed

        return (
          <AnimatedItem
            key={id}
            delay={index * DELAY}
            expanded={displayAdminItem}
            isActive={null}
            href={href}
            target={target ?? "_self"}
          >
            <StyledText>
              <Flex alignItems="center">{t(label)}</Flex>
            </StyledText>
          </AnimatedItem>
        )
      },
    )

  const renderHoverItems = () =>
    getAdminNavItems(isXtmAdmin, isXtpAdmin).map(
      ({ id, label, href, target }) => {
        return (
          <a
            href={href}
            target={target ?? "_self"}
            key={id}
            data-testid="hovered-item"
          >
            <BaseHoverText>{t(label)}</BaseHoverText>
          </a>
        )
      },
    )

  return (
    <>
      <HoverAnchor onMouseEnter={handleHoverIn} onMouseLeave={handleHoverOut}>
        {isCollapsed && isAdminHovered && (
          <HoverContainer>{renderHoverItems()}</HoverContainer>
        )}
        <Sidebar.Item
          icon="CoreSettings"
          onClick={toggleAdmin}
          data-testid={
            isCollapsed
              ? "xtm-sidebar-admin-header-collapsed"
              : "xtm-sidebar-admin-header"
          }
          isActive={null}
          key={8}
          as="div"
        >
          <Flex alignItems="center" justifyContent="space-around">
            <Text>{t(i18n.administrationText)}</Text>
            <AdminArrow rotated={isAdminCollapsed ? "false" : "true"} />
          </Flex>
        </Sidebar.Item>
      </HoverAnchor>
      <ul>{renderAdminItems()}</ul>
    </>
  )
}

const enhance = compose(withi18n(i18n))

export default enhance(OTMAdminSidebarItems)
